import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Heading,
  Tab,
  TabList,
  Tabs,
  VStack
} from '@chakra-ui/react';
import ContentLayout from '@layouts/ContentLayout';
import { kebabCaseToSpaceSeperate } from '@utils';
import AccountSettlements from '@views/SettlementServiceDashboard/AccountSettlements';
import ClientSettlements from '@views/SettlementServiceDashboard/ClientSettlements';
import {
  SETTLEMENT_SERVICE_DASHBOARD_STRINGS,
  TAB_STATES
} from '@views/SettlementServiceDashboard/constant';
import { MdOutlineKeyboardDoubleArrowLeft } from 'react-icons/md';
import LedgerSettlements from '@views/SettlementServiceDashboard/LedgerSettlements';
import ClientAccounts from '@views/SettlementServiceDashboard/ClientAccounts';
import { generalConfig as config } from '@views/SettlementServiceDashboard/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateSettlementServiceClientsReqData,
  updateSettlementServiceLedgersReqData,
  updateSettlementServiceAccountsReqData
} from '@store/actions/reqDataActions';

const SettlementServiceDashboard = () => {
  const dispatch = useDispatch();

  const clientsReqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.clients
  );
  const ledgersReqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.ledgers
  );
  const accountsReqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.accounts
  );

  // Local State
  const [selectedTab, setSelectedTab] = useState(TAB_STATES.CLIENT);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [showRightSidebar, setShowRightSidebar] = useState(false);

  // Get config api
  useEffect(() => {
    fetchDashboardConfig();
  }, []);

  useEffect(() => {
    if (selectedClient || selectedAccount || selectedLedger) {
      setShowRightSidebar(true);
    } else {
      setShowRightSidebar(false);
    }
  }, [selectedClient]);

  const fetchDashboardConfig = () => {
    dispatch(
      config.getDashboardConfig({
        onSuccess: (data) => handleUpdateDashboardReqData(data)
      })
    );
  };

  const handleUpdateDashboardReqData = (data) => {
    const { clients, ledgers, accounts } = data;

    if (clients) {
      const searches = config.searchBuilderBySearchKeys({
        searchKeys: clients?.searchKeys
      });
      // const filtersArray = clients?.filters
      //   ? Object.keys(clients.filters).map((key) => ({
      //       key,
      //       title: key.charAt(0).toUpperCase() + key.slice(1),
      //       filters: clients.filters[key]
      //     }))
      //   : [];

      dispatch(
        updateSettlementServiceClientsReqData({
          ...clientsReqData,
          searches: searches
          // filters: filtersArray
        })
      );
    }

    // Ledgers
    if (ledgers) {
      const searches = config.searchBuilderBySearchKeys({
        searchKeys: ledgers?.searchKeys
      });
      // const filtersArray = ledgers?.filters
      //   ? Object.keys(ledgers.filters).map((key) => ({
      //       key,
      //       title: key.charAt(0).toUpperCase() + key.slice(1),
      //       filters: ledgers.filters[key]
      //     }))
      //   : [];

      dispatch(
        updateSettlementServiceLedgersReqData({
          ...ledgersReqData,
          searches: searches
          // filters: filtersArray
        })
      );
    }

    // Accounts
    if (accounts) {
      const searches = config.searchBuilderBySearchKeys({
        searchKeys: accounts?.searchKeys
      });
      // const filtersArray = accounts?.filters
      //   ? Object.keys(accounts.filters).map((key) => ({
      //       key,
      //       title: key.charAt(0).toUpperCase() + key.slice(1),
      //       filters: accounts.filters[key]
      //     }))
      //   : [];

      dispatch(
        updateSettlementServiceAccountsReqData({
          ...accountsReqData,
          searches: searches
          // filters: filtersArray
        })
      );
    }
  };

  const handleResize = (e) => {
    e?.preventDefault();
    if (showRightSidebar) {
      setShowRightSidebar(false);
    } else {
      setShowRightSidebar(true);
    }
  };

  const handleTabChange = useCallback((e) => {
    setSelectedTab(Object.keys(TAB_STATES)[e]);
  }, []);

  return (
    <ContentLayout>
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          lg: `${showRightSidebar ? 7 : 10}fr 0fr ${showRightSidebar ? 3 : 0}fr`
        }}
      >
        {/* Main Column */}
        <VStack
          overflowY={'hidden'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
        >
          <Heading size="lg" color="colorPrimary">
            {SETTLEMENT_SERVICE_DASHBOARD_STRINGS.HEADING}
          </Heading>
          <Tabs
            variant={'enclosed'}
            size={'md'}
            onChange={handleTabChange}
            defaultIndex={Object.keys(TAB_STATES).indexOf(selectedTab)}
          >
            <TabList color={'whiteAlpha.700'} borderColor={'whiteAlpha.500'}>
              {Object.keys(TAB_STATES).map((tabState, ind) => (
                <Tab
                  key={ind}
                  _selected={{
                    color: 'brand.500',
                    border: '1px solid',
                    borderColor: 'whiteAlpha.500',
                    borderBottom: '1px solid black'
                  }}
                  _focus={{
                    boxShadow: 'none'
                  }}
                >
                  {kebabCaseToSpaceSeperate(tabState)}
                </Tab>
              ))}
            </TabList>
          </Tabs>
          {selectedTab === TAB_STATES.CLIENT && (
            <ClientSettlements
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
            />
          )}
          {selectedTab === TAB_STATES.LEDGER && (
            <LedgerSettlements
              selectedLedger={selectedLedger}
              setSelectedLedger={setSelectedLedger}
            />
          )}
          {selectedTab === TAB_STATES.ACCOUNT && (
            <AccountSettlements
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
            />
          )}
        </VStack>

        <Box alignItems="center" position="relative">
          <Divider
            style={{ margin: '0' }}
            borderColor="whiteAlpha.500"
            orientation="vertical"
            transition="width 0.3s ease-in-out"
          />
          <Button
            colorScheme="brand"
            size="sm"
            bg="black"
            border="2px solid"
            borderColor="whiteAlpha.500"
            borderRadius="xl"
            p={0}
            onClick={(e) => handleResize(e)}
            position={'absolute'}
            top={12}
            left={-4}
          >
            <Box
              as="span"
              transition="transform 0.3s ease-in-out"
              transform={!showRightSidebar ? 'rotate(0deg)' : 'rotate(180deg)'}
              display="inline-block"
            >
              <MdOutlineKeyboardDoubleArrowLeft />
            </Box>
          </Button>
        </Box>

        <VStack
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
        >
          {/*NOTE :: We will only display the client details on the sidebar for now.*/}
          {showRightSidebar && (
            <>
              {selectedTab === TAB_STATES.CLIENT && !!selectedClient && (
                <ClientAccounts
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                />
              )}
            </>
          )}
        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default SettlementServiceDashboard;
